import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { 
  AppBar, 
  Button, 
  Box, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Container,
  Menu,
  MenuItem,
  Badge,
  Popover,
  Paper,
  Fade,
  TextField,
  InputAdornment
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import { useTranslation } from '../hooks/useTranslation';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCheckout } from '../contexts/CheckoutContext';
import { Checkout } from './Checkout';

const StyledNavButton = styled(Button)<{ active?: boolean; component?: React.ElementType; to?: string }>(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: active ? 600 : 400,
  textTransform: 'none',
  margin: theme.spacing(0, 1),
  fontSize: '1rem',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
}));

// const LanguageButton = styled(IconButton)(({ theme }) => ({
//   color: theme.palette.text.primary,
//   fontSize: '1rem',
//   fontWeight: 500,
//   '&:hover': {
//     backgroundColor: 'transparent',
//   },
// }));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const DesktopNav = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const HeaderContent = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}));

const CategoriesButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: active ? 600 : 400,
  textTransform: 'none',
  margin: theme.spacing(0, 1),
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
}));

const CategoriesDropdown = styled(Paper)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  minWidth: 200,
  maxWidth: 300,
  marginTop: theme.spacing(1),
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  borderRadius: 12,
  border: '1px solid rgba(0,0,0,0.08)',
  overflow: 'hidden',
  direction: isRTL ? 'rtl' : 'ltr',
}));

const CategoryMenuItem = styled(MenuItem)<{ isRTL?: boolean; isActive?: boolean }>(({ theme, isRTL, isActive }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.95rem',
  fontWeight: isActive ? 600 : 500,
  textAlign: isRTL ? 'right' : 'left',
  direction: isRTL ? 'rtl' : 'ltr',
  justifyContent: isRTL ? 'flex-end' : 'flex-start',
  backgroundColor: isActive ? 'rgba(205, 127, 50, 0.12)' : 'transparent',
  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
  position: 'relative',
  '& .MuiMenuItem-root': {
    textAlign: isRTL ? 'right' : 'left',
  },
  '&:hover': {
    backgroundColor: isActive ? 'rgba(205, 127, 50, 0.18)' : 'rgba(205, 127, 50, 0.08)',
  },
  '&:first-of-type': {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  '&:last-of-type': {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  '&::before': isActive ? {
    content: '""',
    position: 'absolute',
    [isRTL ? 'right' : 'left']: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 4,
    height: '60%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '0 2px 2px 0',
  } : {},
}));

const CategoryIconWrapper = styled(Box)<{ isRTL?: boolean }>(({ theme, isRTL }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  justifyContent: isRTL ? 'flex-end' : 'flex-start',
  direction: isRTL ? 'rtl' : 'ltr',
}));

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentLang } = useLanguage();
  //const { currentLang, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { items } = useCheckout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [checkoutAnchorEl, setCheckoutAnchorEl] = useState<null | HTMLElement>(null);
  const [categoriesAnchorEl, setCategoriesAnchorEl] = useState<null | HTMLElement>(null);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const navItems = [
    // { key: 'tutorials', label: t('header.nav.tutorials'), path: '/tutorials' },
    // { key: 'workshops', label: t('header.nav.workshops'), path: '/workshops' },
    // { key: 'shop', label: t('header.nav.shop'), path: '/shop' },
    { key: 'chat', label: t('header.nav.chat'), path: '/chat' },
    { key: 'freeTools', label: t('header.nav.freeTools'), path: '/free-tools' },
    { key: 'about', label: t('header.nav.about'), path: '/about' },
    { key: 'contact', label: t('header.nav.contact'), path: '/contact' }
  ];

  // My Orders item - always show
  const myOrdersItem = { key: 'myOrders', label: t('header.nav.myOrders'), path: '/my-orders' };
  
  // Deals item - always show
  const dealsItem = { key: 'deals', label: t('header.nav.deals'), path: '/deals' };

  const categoryItems = [
    { key: 'cricut', label: t('header.nav.cricut'), path: '/category/cricut' },
    { key: 'goldsmith', label: t('header.nav.goldsmith'), path: '/category/goldsmith' },
    { key: 'teva', label: t('header.nav.teva'), path: '/category/teva' },
    { key: 'modeling3d', label: t('header.nav.modeling3d'), path: '/category/modeling3d' },
    { key: 'svgfiles', label: t('header.nav.svgfiles'), path: '/category/svgfiles' },
    { key: 'sublimation', label: t('header.nav.sublimation'), path: '/category/sublimation' }//,
    // { key: 'others', label: t('header.nav.others'), path: '/category/others' }
  ];

  // Check if any category is currently active
  const isCategoryActive = categoryItems.some(item => location.pathname === item.path);

  // Create ordered nav items for mobile (My Orders, Deals, then categories, then other nav items)
  const orderedNavItems = [myOrdersItem, dealsItem, ...categoryItems, ...navItems];
  
  // Add admin link if user is super admin
  if (user?.role === 'superAdmin') {
    orderedNavItems.push({ key: 'admin', label: t('header.nav.admin'), path: '/admin' });
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogin = () => {
    navigate('/login', { state: { from: location } });
  };

  const handleCheckoutClick = (event: React.MouseEvent<HTMLElement>) => {
    setCheckoutAnchorEl(event.currentTarget);
  };

  const handleCheckoutClose = () => {
    setCheckoutAnchorEl(null);
  };

  const handleCategoriesOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCategoriesAnchorEl(event.currentTarget);
  };

  const handleCategoriesClose = () => {
    setCategoriesAnchorEl(null);
  };

  const handleCategoryClick = (path: string) => {
    navigate(path);
    handleCategoriesClose();
  };

  const handleSearchOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSearchAnchorEl(event.currentTarget);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
    setSearchTerm('');
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      handleSearchClose();
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ 
      textAlign: currentLang === 'he' ? 'right' : 'left',
      direction: currentLang === 'he' ? 'rtl' : 'ltr'
    }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Menu
      </Typography>
      <Divider />
      <List>
        {orderedNavItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                textAlign: currentLang === 'he' ? 'right' : 'left',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(205, 127, 50, 0.08)',
                },
              }}
            >
              <ListItemText 
                primary={item.label}
                sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleSearchOpen}>
            <SearchIcon sx={{ mr: currentLang === 'he' ? 0 : 2, ml: currentLang === 'he' ? 2 : 0 }} />
            <ListItemText 
              primary={t('header.search')}
              sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
            />
          </ListItemButton>
        </ListItem>
        {/* <ListItem disablePadding>
          <ListItemButton onClick={() => setLanguage(currentLang === 'en' ? 'he' : 'en')}>
            <ListItemText 
              primary={currentLang === 'en' ? 'עברית' : 'English'}
              sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
            />
          </ListItemButton>
        </ListItem> */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/checkout')}>
            <Badge badgeContent={totalItems} color="primary">
              <ShoppingCartIcon sx={{ mr: currentLang === 'he' ? 0 : 2, ml: currentLang === 'he' ? 2 : 0 }} />
            </Badge>
            <ListItemText 
              primary={t('header.cart')}
              sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
            />
          </ListItemButton>
        </ListItem>
        {isAuthenticated ? (
          <>
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemText 
                  primary={`${user?.firstName} ${user?.lastName}`}
                  sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate('/edit-profile'); setMobileOpen(false); }}>
                <AccountCircleIcon sx={{ mr: currentLang === 'he' ? 0 : 2, ml: currentLang === 'he' ? 2 : 0 }} />
                <ListItemText 
                  primary={t('profile.editProfile')}
                  sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <LogoutIcon sx={{ mr: currentLang === 'he' ? 0 : 2, ml: currentLang === 'he' ? 2 : 0 }} />
                <ListItemText 
                  primary={t('header.logout')}
                  sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
                />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogin}>
              <ListItemText 
                primary={t('header.login')}
                sx={{ textAlign: currentLang === 'he' ? 'right' : 'left' }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" color="default" elevation={0}>
      <Container sx={{ maxWidth: '100% !important' }}>
        <HeaderContent sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
          {/* Mobile Layout: Cart - Title - Menu */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            width: '100%'
          }}>
            {/* Mobile Cart - Left in LTR, Right in RTL */}
            <IconButton
              size="large"
              aria-label={t('header.cart')}
              color="inherit"
              onClick={handleCheckoutClick}
              sx={{ 
                order: currentLang === 'he' ? 3 : 1,
                visibility: location.pathname === '/checkout' ? 'hidden' : 'visible'
              }}
            >
              <Badge badgeContent={totalItems} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            
            {/* Mobile Title - Always in center */}
            <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit', order: 2 }}>
              <Typography variant="h6" component="div">
                {t('header.logo')}
              </Typography>
            </RouterLink>
            
            {/* Mobile Menu - Right in LTR, Left in RTL */}
            <MobileMenuButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ order: currentLang === 'he' ? 1 : 3 }}
            >
              <MenuIcon />
            </MobileMenuButton>
          </Box>

          {/* Mobile Cart Popover */}
          <Popover
            open={Boolean(checkoutAnchorEl)}
            anchorEl={checkoutAnchorEl}
            onClose={handleCheckoutClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: currentLang === 'he' ? 'right' : 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: currentLang === 'he' ? 'right' : 'left',
            }}
            sx={{ 
              display: { xs: 'block', md: 'none' },
              visibility: location.pathname === '/checkout' ? 'hidden' : 'visible'
            }}
          >
            <Checkout onClose={handleCheckoutClose} />
          </Popover>

          {/* Desktop Layout */}
          <Logo sx={{ display: { xs: 'none', md: 'flex' } }}>
            <RouterLink to="/">
              <Typography variant="h6" component="div">
                {t('header.logo')}
              </Typography>
            </RouterLink>
          </Logo>

          <DesktopNav>
            <CategoriesButton
              onClick={handleCategoriesOpen}
              active={isCategoryActive}
              endIcon={<ExpandMoreIcon />}
            >
              <CategoryIcon />
              {t('header.nav.categories')}
            </CategoriesButton>
            <Menu
              anchorEl={categoriesAnchorEl}
              open={Boolean(categoriesAnchorEl)}
              onClose={handleCategoriesClose}
              TransitionComponent={Fade}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: currentLang === 'he' ? 'right' : 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: currentLang === 'he' ? 'right' : 'left',
              }}
              PaperProps={{
                component: CategoriesDropdown,
                isRTL: currentLang === 'he',
                sx: {
                  direction: currentLang === 'he' ? 'rtl' : 'ltr',
                }
              }}
            >
              {categoryItems.map((item) => (
                <CategoryMenuItem
                  key={item.key}
                  onClick={() => handleCategoryClick(item.path)}
                  selected={location.pathname === item.path}
                  isRTL={currentLang === 'he'}
                  isActive={location.pathname === item.path}
                >
                  <CategoryIconWrapper isRTL={currentLang === 'he'}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        textAlign: currentLang === 'he' ? 'right' : 'left',
                        direction: currentLang === 'he' ? 'rtl' : 'ltr',
                        width: '100%',
                        display: 'block'
                      }}
                    >
                      {item.label}
                    </Typography>
                  </CategoryIconWrapper>
                </CategoryMenuItem>
              ))}
            </Menu>
            <StyledNavButton
              component={RouterLink}
              to={myOrdersItem.path}
              active={location.pathname === myOrdersItem.path}
            >
              {myOrdersItem.label}
            </StyledNavButton>
            <StyledNavButton
              component={RouterLink}
              to="/deals"
              active={location.pathname === '/deals'}
            >
              {t('header.nav.deals')}
            </StyledNavButton>
            {navItems.map((item) => (
              <StyledNavButton
                key={item.path}
                component={RouterLink}
                to={item.path}
                active={location.pathname === item.path}
              >
                {item.label}
              </StyledNavButton>
            ))}
            {user?.role === 'superAdmin' && (
              <StyledNavButton
                component={RouterLink}
                to="/admin"
                active={location.pathname === '/admin'}
              >
                {t('header.nav.admin')}
              </StyledNavButton>
            )}
          </DesktopNav>

          <ActionButtons>
            <IconButton
              size="large"
              aria-label={t('header.search')}
              color="inherit"
              onClick={handleSearchOpen}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <SearchIcon />
            </IconButton>
            {/* <LanguageButton
              onClick={() => setLanguage(currentLang === 'en' ? 'he' : 'en')}
              aria-label={t('header.language')}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {currentLang === 'en' ? 'עב' : 'EN'}
            </LanguageButton> */}
            <IconButton
              size="large"
              aria-label={t('header.cart')}
              color="inherit"
              onClick={handleCheckoutClick}
              sx={{ 
                display: { xs: 'none', md: 'flex' },
                visibility: location.pathname === '/checkout' ? 'hidden' : 'visible'
              }}
            >
              <Badge badgeContent={totalItems} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            <Popover
              open={Boolean(checkoutAnchorEl)}
              anchorEl={checkoutAnchorEl}
              onClose={handleCheckoutClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{ 
                display: { xs: 'none', md: 'block' },
                visibility: location.pathname === '/checkout' ? 'hidden' : 'visible'
              }}
            >
              <Checkout onClose={handleCheckoutClose} />
            </Popover>
            {isAuthenticated ? (
              <>
                <IconButton
                  size="large"
                  aria-label="account menu"
                  onClick={handleMenuOpen}
                  color="inherit"
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem disabled>
                    {user?.firstName} {user?.lastName}
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/edit-profile'); handleMenuClose(); }}>
                    <AccountCircleIcon sx={{ mr: 1 }} />
                    {t('profile.editProfile')}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1 }} />
                    {t('header.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="primary"
                onClick={handleLogin}
                sx={{ ml: 1, display: { xs: 'none', md: 'block' } }}
              >
                {t('header.login')}
              </Button>
            )}
          </ActionButtons>
        </HeaderContent>
      </Container>

      {/* Search Popover */}
      <Popover
        open={Boolean(searchAnchorEl)}
        anchorEl={searchAnchorEl}
        onClose={handleSearchClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: currentLang === 'he' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: currentLang === 'he' ? 'right' : 'left',
        }}
        sx={{ 
          '& .MuiPopover-paper': {
            p: 2,
            minWidth: { xs: 280, sm: 350, md: 450 },
            maxWidth: { xs: 320, sm: 400, md: 600 },
            width: { xs: '90vw', sm: 'auto' },
          }
        }}
      >
        <Box component="form" onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            placeholder={t('header.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
            size="medium"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="submit"
                    edge="end"
                    disabled={!searchTerm.trim()}
                    size="medium"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                direction: currentLang === 'he' ? 'rtl' : 'ltr',
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                padding: { xs: '8px 12px', sm: '10px 14px', md: '12px 14px' },
                minHeight: { xs: '48px', sm: '52px', md: '56px' },
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
              }
            }}
          />
        </Box>
      </Popover>

      <Drawer
        variant="temporary"
        anchor={currentLang === 'he' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            direction: currentLang === 'he' ? 'rtl' : 'ltr'
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}; 